import {
    Component,
    OnInit,
    Inject,
    Input,
    ViewChild,
    ElementRef,
    AfterViewInit,
    NgZone
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AlertService } from 'app/services/alert.service';
import { Subject } from 'rxjs';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';

@Component({
    selector: 'app-upload-files-dialog',
    templateUrl: './upload-files-dialog.component.html',
    styleUrls: ['./upload-files-dialog.component.scss']
})
export class UploadFilesDialogComponent implements OnInit, AfterViewInit {
    @ViewChild('imageElement', { static: false }) imageElement!: ElementRef<HTMLImageElement>;

    // Dados do crop/zoom
    private cropper!: Cropper;
    imageUrl: string | null = null;
    scale = 1;
    minScale = 1;

    // inputs via data
    @Input() resizeToWidth!: number;
    @Input() resizeToHeight!: number;
    title = 'Upload de imagem';
    description = 'Selecione uma imagem. Arraste ou clique aqui';

    // estado
    disabled = false;
    borderBlue = false;
    loading = false;

    public onImageSaveCalled = new Subject<File>();

    constructor(
        private _alertService: AlertService,
        public dialogRef: MatDialogRef<UploadFilesDialogComponent>,
        private _zone: NgZone,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) { }

    ngOnInit(): void {
        if (this.data.title) this.title = this.data.title;
        if (this.data.description) this.description = this.data.description;
        if (this.data.resizeToWidth) this.resizeToWidth = this.data.resizeToWidth;
        if (this.data.resizeToHeight) this.resizeToHeight = this.data.resizeToHeight;
    }

    ngAfterViewInit() {
        // O Cropper só será inicializado após carregar a imagem
    }

    allowDrop(e: DragEvent): void {
        e.preventDefault();
        this.borderBlue = true;
    }

    fileChangeEvent(event: any): void {
        const file: File = event.target.files[0];
        if (!file) return;


        // if (file.size > 5 * 1024 * 1024) {
        //   this._alertService.error('Tamanho máximo 5 MB.');
        //   return;
        // }

        const reader = new FileReader();
        reader.onload = () => {
            this.imageUrl = reader.result as string;
            this.disabled = true;
            setTimeout(() => this.initCropper(), 0);
        };
        reader.readAsDataURL(file);
    }

    private initCropper(): void {
        if (this.cropper) {
            this.cropper.destroy();
        }

        this.cropper = new Cropper(this.imageElement.nativeElement, {
            aspectRatio: this.resizeToWidth / this.resizeToHeight,
            viewMode: 0,            // ← sem restrições
            dragMode: 'move',
            autoCropArea: 1,
            background: true,
            movable: true,          // permite pan livre
            scalable: false,
            cropBoxMovable: false,
            cropBoxResizable: false,
            toggleDragModeOnDblclick: false,

            // zera todos os mínimos
            minCropBoxWidth: 0,
            minCropBoxHeight: 0,
            minCanvasWidth: 0,
            minCanvasHeight: 0,

            ready: () => {
                // pega dimensões naturais da imagem
                const imgData = this.cropper.getImageData();
                // pega dimensões do crop-box (a área de corte visível)
                const boxData = this.cropper.getCropBoxData();

                // calcula escala em X e Y
                const scaleX = boxData.width / imgData.naturalWidth;
                const scaleY = boxData.height / imgData.naturalHeight;
                // escolhe o **maior** (cover)
                this.scale = Math.min(scaleX, scaleY);

                // aplica zoom
                this.cropper.zoomTo(this.scale);
            }
        });
    }

    onZoomSliderChange(value: number): void {
        this.scale = value;
        if (this.cropper) {
            this.cropper.zoomTo(this.scale);
        }
    }

    /** Volta para a seleção de arquivo */
    chooseAnother(): void {
        this.cropper?.destroy();
        this.cropper = undefined!;
        this.imageUrl = null;
        this.disabled = false;
        this.borderBlue = false;
    }

    close(): void {
        this.dialogRef.close();
    }

    save(): void {
        if (!this.cropper) return;
        this.loading = true;

        const canvas = this.cropper.getCroppedCanvas({
            width: this.resizeToWidth,
            height: this.resizeToHeight,
            fillColor: undefined, // mantém transparente    
            imageSmoothingQuality: 'high'
        });

        // o callback do toBlob roda fora da zone do Angular: volta para ela para a tela atualizar
        canvas.toBlob(blob => this._zone.run(() => {
            if (!blob) {
                this._alertService.error('Falha ao gerar a imagem.');
                this.loading = false;
                return;
            }
            const file = new File([blob], 'cropped.png', { type: 'image/png' });
            this.onImageSaveCalled.next(file);
        }));
    }
}
